type DatedTitled = {
  title: string;
  plannedEndDate?: string | null;
  plannedStartDate?: string | null;
};

export type PlanningSortParent = DatedTitled & {
  id: string;
  objectiveId?: string | null;
  parentWorkItemId?: string | null;
};

export function compareAlphanumeric(left: string, right: string): number {
  const leftValue = left.trim();
  const rightValue = right.trim();
  if (!leftValue && rightValue) return 1;
  if (leftValue && !rightValue) return -1;
  return leftValue.localeCompare(rightValue, 'pt-BR', { numeric: true, sensitivity: 'base' });
}

function compareOptionalIsoDate(left?: string | null, right?: string | null): number {
  const leftDate = left?.trim() ?? '';
  const rightDate = right?.trim() ?? '';
  if (leftDate && rightDate && leftDate !== rightDate) {
    return leftDate.localeCompare(rightDate);
  }

  if (leftDate && !rightDate) return -1;
  if (!leftDate && rightDate) return 1;
  return 0;
}

export function compareDateThenTitle(left: DatedTitled, right: DatedTitled): number {
  const endDiff = compareOptionalIsoDate(left.plannedEndDate, right.plannedEndDate);
  if (endDiff !== 0) return endDiff;

  const startDiff = compareOptionalIsoDate(left.plannedStartDate, right.plannedStartDate);
  if (startDiff !== 0) return startDiff;

  return compareAlphanumeric(left.title, right.title);
}

export function sortPlanningObjectives<T extends { title: string; cycleId: string }>(
  items: T[],
  cycles: Array<{ id: string; name: string }>,
): T[] {
  const cycleNameById = new Map(cycles.map((cycle) => [cycle.id, cycle.name]));

  return [...items].sort((left, right) => {
    const cycleDiff = compareAlphanumeric(
      cycleNameById.get(left.cycleId) ?? '',
      cycleNameById.get(right.cycleId) ?? '',
    );
    if (cycleDiff !== 0) return cycleDiff;

    return compareAlphanumeric(left.title, right.title);
  });
}

export function sortPlanningWorkItems<
  T extends DatedTitled & {
    cycleId?: string | null;
    objectiveId?: string | null;
    parentWorkItemId?: string | null;
  },
>(
  items: T[],
  context: {
    cycles?: Array<{ id: string; name: string }>;
    objectives: Array<{ id: string; title: string }>;
    parents: PlanningSortParent[];
  },
): T[] {
  const cycleNameById = new Map((context.cycles ?? []).map((item) => [item.id, item.name]));
  const objectiveTitleById = new Map(context.objectives.map((item) => [item.id, item.title]));
  const parentById = new Map(context.parents.map((item) => [item.id, item]));

  function parentOf(item: T): PlanningSortParent | undefined {
    return item.parentWorkItemId ? parentById.get(item.parentWorkItemId) : undefined;
  }

  function metaOf(item: T): PlanningSortParent | undefined {
    const parent = parentOf(item);
    if (!parent) return undefined;
    return parent.parentWorkItemId
      ? parentById.get(parent.parentWorkItemId) ?? parent
      : parent;
  }

  function marcoOf(item: T): PlanningSortParent | undefined {
    const parent = parentOf(item);
    if (!parent?.parentWorkItemId) return undefined;
    return parent;
  }

  return [...items].sort((left, right) => {
    const cycleDiff = compareAlphanumeric(
      cycleNameById.get(left.cycleId ?? '') ?? '',
      cycleNameById.get(right.cycleId ?? '') ?? '',
    );
    if (cycleDiff !== 0) return cycleDiff;

    const objectiveDiff = compareAlphanumeric(
      objectiveTitleById.get(left.objectiveId ?? '') ?? '',
      objectiveTitleById.get(right.objectiveId ?? '') ?? '',
    );
    if (objectiveDiff !== 0) return objectiveDiff;

    const leftMeta = metaOf(left);
    const rightMeta = metaOf(right);
    if (leftMeta || rightMeta) {
      const metaDiff = compareDateThenTitle(leftMeta ?? { title: '' }, rightMeta ?? { title: '' });
      if (metaDiff !== 0) return metaDiff;
    }

    const leftMarco = marcoOf(left);
    const rightMarco = marcoOf(right);
    if (leftMarco || rightMarco) {
      const marcoDiff = compareDateThenTitle(leftMarco ?? { title: '' }, rightMarco ?? { title: '' });
      if (marcoDiff !== 0) return marcoDiff;
    }

    return compareDateThenTitle(left, right);
  });
}
